'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import dynamic from 'next/dynamic';
import { useFetchHotelStatic, useIsHotelStaticLoading } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import EvoHotelDetailsGallery from '@/app/components/hotel-detail/evo/Hero/EvoHotelDetailsGallery';
import { EvoTopSecretGallery } from '@/app/components/hotel-detail/evo/Hero/EvoTopSecretGallery';
import { CheckTime, hasCheckTimes } from '@/app/components/hotel-detail/HotelCheckinCheckout';
import { HotelPageContentSection, Title } from '@/app/components/hotel-detail/HotelPageContentSection';
import { SeeAllButton } from '@/app/components/hotel-detail/common/button';
import { useTranslation } from '@/app/components/translations/Translation';
import HotelPagePoliciesSection from '@/app/components/hotel-detail/HotelPagePoliciesSection';
import HotelPageOverviewSection, { hasOverviewContent } from '@/app/components/hotel-detail/HotelPageOverviewSection';
import {
  HotelPageEmissionsSectionEvo,
  useHasSustainabilitySection,
} from '@/app/components/hotel-detail/HotelPageEmissionsSection';
import { HotelPageBeforeYouBookSectionEvo } from '@/app/components/hotel-detail/HotelPageBeforeYouBookSection';
import { GuestSummarizedReview } from '@/app/components/hotel-detail/GuestSummarizedReview';
import { useFetchGuestSummarizedReviews } from '@/app/components/hotel-detail/hooks/useFetchGuestSummarizedReviews';
import { useHasGuestReviews } from '@/app/components/hotel-detail/hooks/useFetchGuestReviews';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useHotelStayDates } from '@/app/components/hotel-detail/hooks/useHotelStayDates';
import { getCheckinCheckoutDates, URL_DATE_FORMAT } from '@/app/components/hotel-detail/common/dates';
import { dynamicImportCatch } from '@/utils/appUtilsClient';
import EvoHotelFacilities, { MostPopularRow } from './EvoHotelFacilities';
import { type EvoFacilityChip, useEvoFacilities } from './useEvoFacilities';
import { MODAL_SECTION_IDS } from './EvoHotelDetailsHeader';
import { EvoWhatsAround, useHasWhatsAround } from './EvoWhatsAround';
import { EvoPropertySurroundings, EVO_SURROUNDINGS_SECTION_ID, useHasSurroundings } from './EvoPropertySurroundings';
import { EvoLocationMap, EvoLocationAddress } from './EvoLocationMap';

const HotelPageReviewSection = dynamic(
  () => import('@/app/components/hotel-detail/HotelPageReviewSection').catch(dynamicImportCatch),
  { ssr: false }
);

const HotelPageGuestReviewsSection = dynamic(
  () => import('@/app/components/hotel-detail/HotelPageGuestReviewsSection').catch(dynamicImportCatch),
  { ssr: false }
);

// #region styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* The one inset shared by every tab, on all four sides: space200 on mobile, space300 from sm up.
     The lateral value matches the tab header's own inset (EvoHotelDetailsHeader), so the titles
     share the edge the header row and its hairline sit on (the tab labels add their own 12px, so
     they start inside it); top and bottom are the design's symmetric py (Figma Content frame,
     py-24 px-24). Owning the bottom here is what gives the Facilities, Location and Reviews tabs
     the same end inset as Overview and Policies — SectionColumn adds none of its own.
     The sections below are flushed (see SectionColumn) so none add their own top padding/margin. */
  padding: ${({ theme }) => theme.kallisto.space.space200};

  /* The See-all target is a heading, not a control, so it takes no ring. */
  h2,
  h3,
  h4,
  h5,
  h6 {
    &[tabindex='-1']:focus {
      outline: none;
    }
  }

  @media ${customCss.breakpoints.sm} {
    padding: ${({ theme }) => theme.kallisto.space.space300};
  }
`;

// A tab's content as a single column with the design's 40px rhythm. Container supplies
// the outer inset; the flex gap is the only vertical spacing between blocks, so the
// dividers (1px children) get an even 40px on each side — matching the Figma Content
// frame (gap-40, py-24, px-24).
const SectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space300};

  /* Sections shared with the full hotel page (facilities / emissions / about / policies /
     before-you-book / reviews) carry their own page-level outer margins & padding. Flatten
     that here so every block lines up on the same edge, the gap is the only vertical rhythm, and
     the tab's top spacing stays exactly the container's inset — nothing added.
     Reviews nests its HotelPageContentSection one level deeper, so reach that too. */
  > .modal-flush-section,
  > .modal-flush-section > section {
    margin: 0;
    padding: 0;
  }

  /* Every tab section title on the tab's own rhythm: nothing above it (the container's inset and
     the column gap already place the block) and space150 below. There is no tablet step — the
     designs have one treatment for mobile and tablet and another for desktop, so this switches to
     space300 at lg, not at sm. The shared Title keeps its page-level 12px/16px for the hotel-page
     layouts that render the same sections. */
  ${Title} {
    padding: 0 0 ${({ theme }) => theme.kallisto.space.space150};
  }

  @media ${customCss.breakpoints.sm} {
    gap: ${({ theme }) => theme.kallisto.space.space500};
  }

  @media ${customCss.breakpoints.lg} {
    ${Title} {
      padding: 0 0 ${({ theme }) => theme.kallisto.space.space300};
    }
  }
`;

const Divider = styled.hr`
  margin: 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
`;

// Mobile Location tab: address above the map, 8px apart (Figma node 979:27519).
const MobileAddressMap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GalleryWrap = styled.div`
  width: 100%;
`;

/* Two columns only from lg. The pair doesn't fit side by side at tablet, so tablet keeps the
   mobile layout — stacked, full width, mobile type and spacing — and the whole block switches to
   the desktop treatment at 1024 in one step. LeftCol, RightCol, ColDivider and the two column
   titles all follow this same gate. (MPH-839) */
const TwoColBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${customCss.breakpoints.lg} {
    flex-direction: row;
    align-items: flex-start;
    gap: 40px;
  }
`;

const LeftCol = styled.div`
  @media ${customCss.breakpoints.lg} {
    flex: 1;
  }
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  @media ${customCss.breakpoints.lg} {
    flex: 1;
  }
`;

const RightColDivider = styled.hr`
  border: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  margin: 20px 0;
`;

/* Horizontal rule between the stacked blocks up to lg, vertical rule between the columns from lg,
   following TwoColBlock's own gate. */
const ColDivider = styled.hr`
  align-self: stretch;
  border: 0;
  margin: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};

  @media ${customCss.breakpoints.lg} {
    width: 1px;
    border-top: 0;
    border-left: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  }
`;

/* Sits beside EvoWhatsAround's SectionTitle once TwoColBlock becomes two columns at lg, with the
   same type and the same gap below it, so the two columns' first content rows land on the same line.
   Up to lg it is a full-width mobile block: Title3 and space150, no tablet step. From lg it takes
   the sub-block treatment — Title4 and space200, below the section titles' space300. (MPH-839) */
const CheckinCheckoutTitle = styled(Text).attrs({ variant: 'Title4', color: 'textPrimary', forceTag: 'h3' })`
  display: block;
  margin-bottom: ${({ theme }) => theme.kallisto.space.space150};
  ${({ theme }) => theme.kallisto.mixins.font('Title3')};

  @media ${customCss.breakpoints.lg} {
    margin-bottom: ${({ theme }) => theme.kallisto.space.space200};
    ${({ theme }) => theme.kallisto.mixins.font('Title4')};
  }
`;

const CheckinBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* The dates and the times are two independent rows rather than two stacked columns: a long
   time label (e.g. "hasta las 12:00 h") no longer widens the check-in/out sides, so the
   dashed connector keeps spanning the gap between the two dates. */
const CheckinBlockRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

/* Wider gap than the times row: 16px sets each date off from its dashed line, while the nights
   label keeps the tighter space100 on both sides (see NightsBadge). */
const DatesRow = styled(CheckinBlockRow)`
  gap: ${({ theme }) => theme.kallisto.space.space200};
`;

const DateText = styled(Text).attrs({ variant: 'Body', weight: 'bold' })`
  white-space: nowrap;
`;

/* Half of the row each, so the times sit under their own date and the loading placeholder
   (sized in %) has a definite width to resolve against. CheckTime's spans are nowrap by
   default, which on a narrow phone let a long localized time overprint the right-aligned
   one; confined to a fixed half it has to wrap instead. */
const CheckTimeCell = styled(Text).attrs({ variant: 'Body' })`
  flex: 1;
  min-width: 0;

  span {
    white-space: normal;
  }
`;

/* No gap: the badge is conditional, so when nights is 0 the two dashed lines are adjacent and
   must join seamlessly. The space100 on each side of the label comes from NightsBadge's own
   padding instead, which disappears with it. */
const NightsPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
`;

const NightsLine = styled.div`
  flex: 1;
  height: 1px;
  border-top: 1px dashed ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  min-width: 16px;
`;

const NightsBadge = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  padding: 0 ${({ theme }) => theme.kallisto.space.space100};
  white-space: nowrap;
`;

// #endregion

function CheckinCheckoutWithNights() {
  // The hotel stay, not the URL (flight) dates: this modal opens from the Hero
  // panel, which already shows the stay dates on a DP partial stay.
  const { checkin, checkout, nights } = useHotelStayDates();
  const { localization } = usePageWrapperData();
  const { data: staticData } = useFetchHotelStatic();
  const isStaticDataLoading = useIsHotelStaticLoading();
  const { t } = useTranslation();

  const { checkin: checkinLabel, checkout: checkoutLabel } = getCheckinCheckoutDates(
    localization.locale,
    checkin,
    checkout,
    URL_DATE_FORMAT,
    { weekday: 'short', day: 'numeric', month: 'short' }
  );

  // The dates row above already carries the stay, so a hotel with no published times just loses
  // this row rather than keeping two placeholder bars that never resolve (MPH-1007).
  const showCheckTimes =
    isStaticDataLoading ||
    hasCheckTimes(staticData?.checkinInformation) ||
    hasCheckTimes(staticData?.checkoutInformation);

  return (
    <div>
      <CheckinCheckoutTitle>{t('vacation.select.details.checkincheckout.title')}</CheckinCheckoutTitle>
      <CheckinBlock>
        <DatesRow>
          <DateText>{checkinLabel}</DateText>
          <NightsPill>
            <NightsLine />
            {nights > 0 && (
              <NightsBadge>
                {nights === 1
                  ? t('vacation.select.cc.hotel.nights.single')
                  : t('vacation.select.cc.hotel.nights', { count: nights.toString() })}
              </NightsBadge>
            )}
            <NightsLine />
          </NightsPill>
          <DateText>{checkoutLabel}</DateText>
        </DatesRow>
        {showCheckTimes && (
          <CheckinBlockRow>
            <CheckTimeCell>
              <CheckTime
                from={staticData?.checkinInformation?.from}
                to={staticData?.checkinInformation?.to}
                isLoading={isStaticDataLoading}
              />
            </CheckTimeCell>
            <CheckTimeCell
              css={`
                text-align: right;
              `}
            >
              <CheckTime
                from={staticData?.checkoutInformation?.from}
                to={staticData?.checkoutInformation?.to}
                isLoading={isStaticDataLoading}
              />
            </CheckTimeCell>
          </CheckinBlockRow>
        )}
      </CheckinBlock>
    </div>
  );
}

// The guest reviews section self-hides until its reviews arrive (and stays hidden for a hotel without
// sellable provider reviews) — its divider must follow it, or a stray line closes the Reviews tab.
// Split out so the fetch only starts once that tab is shown.
function GuestReviewsDivider() {
  return useHasGuestReviews() ? <Divider /> : null;
}

// The Overview tab's facilities block: a compact preview (most popular facilities only) plus a
// link through to the dedicated Facilities tab which holds the full list.
function OverviewFacilitiesPreview({ chips, onSeeAll }: { chips: EvoFacilityChip[]; onSeeAll: () => void }) {
  const { t } = useTranslation();

  return (
    <HotelPageContentSection className="modal-flush-section" title={t('vacation.select.facilities.title')}>
      <MostPopularRow chips={chips} />
      <SeeAllButton
        onClick={onSeeAll}
        css={`
          margin-top: 8px;
        `}
      >
        {t('vacation.select.cc.hotel_info.facilities.see_all')}
      </SeeAllButton>
    </HotelPageContentSection>
  );
}

// tabindex only while it holds focus, so the heading never joins the Tab order.
function focusHeadingIn(containerId: string) {
  const heading = document.getElementById(containerId)?.querySelector<HTMLElement>('h2, h3, h4, h5, h6');
  if (!heading) return;
  heading.setAttribute('tabindex', '-1');
  heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
  heading.focus({ preventScroll: true });
}

export function EvoHotelDetailsModalContent({
  activeSection = MODAL_SECTION_IDS.details,
  isMobile = false,
  onSelectSection,
}: { activeSection?: string; isMobile?: boolean; onSelectSection?: (id: string) => void } = {}) {
  const { data: staticData } = useFetchHotelStatic();
  const { localization } = usePageWrapperData();
  const { reviewsDisabled } = localization.configValuesApp;
  // The AI summary (GuestSummarizedReview) is behind a disabled-by-default flag, and the
  // hook skips the fetch entirely while it's off. Default to the compact side-by-side
  // layout and only switch to the dedicated two-column one once a review is actually there.
  const { data: aiReview } = useFetchGuestSummarizedReviews();
  const showAiSummary = !!aiReview?.review;
  const hasWhatsAround = useHasWhatsAround();
  const facilities = useEvoFacilities();
  const hasSustainability = useHasSustainabilitySection();
  const { atol } = useInitialParams();
  const { accommodationOnly } = useUrlSearchParams();
  // Mirror HotelPageBeforeYouBookSectionEvo's own render guard so the divider never orphans — which
  // means following it onto Top Secret too (MPH-935): the section no longer needs ATOL to have
  // something to say, and a Top Secret stay is hotel-only, so an ATOL-only guard here would drop the
  // Top Secret terms from the modal on exactly the product that has to carry them.
  const showBeforeYouBook = (!accommodationOnly && !!atol) || !!staticData?.isTopSecret;
  // Property surroundings (map + address) is a desktop-only section between facilities and emissions.
  // When present, the What's-around block's "See all" link scrolls here instead of opening the map.
  const hasSurroundings = useHasSurroundings();
  const showSurroundings = !isMobile && hasSurroundings;
  const scrollToSurroundings = () => {
    document.getElementById(EVO_SURROUNDINGS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    focusHeadingIn(EVO_SURROUNDINGS_SECTION_ID);
  };
  // The link unmounts with the Overview tab, so render the new tab first and hand it focus (QACOE-965).
  const seeAllTab = (id: string) => () => {
    flushSync(() => onSelectSection?.(id));
    focusHeadingIn(id);
  };
  // The What's-around "See all" link: on mobile it jumps to the dedicated Location tab; on desktop
  // it smooth-scrolls to the Property surroundings section (undefined → EvoWhatsAround opens the map).
  const handleSeeAll = isMobile
    ? seeAllTab(MODAL_SECTION_IDS.location)
    : showSurroundings
      ? scrollToSurroundings
      : undefined;
  const containerRef = useRef<HTMLDivElement>(null);

  const uniqueImages = useMemo(
    () => (staticData?.images ? Array.from(new Set(staticData.images)) : []),
    [staticData?.images]
  );

  useEffect(() => {
    document.body.classList.add('evo-hotel-details-modal-open');
    return () => document.body.classList.remove('evo-hotel-details-modal-open');
  }, []);

  // Switching tabs swaps the visible section in place — reset the modal's scroll
  // container to the top so the new section starts from its beginning.
  useEffect(() => {
    let scrollParent = containerRef.current?.parentElement ?? null;
    while (scrollParent) {
      const { overflowY } = getComputedStyle(scrollParent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParent.scrollTop = 0;
        break;
      }
      scrollParent = scrollParent.parentElement;
    }
  }, [activeSection]);

  if (!staticData) return null;

  return (
    <Container ref={containerRef}>
      {/* Desktop groups everything under the "Details & Facilities" tab; mobile shows the same
          content under its first "Overview" tab, except facilities is reduced to a preview that
          links out to the dedicated Facilities tab. */}
      {((!isMobile && activeSection === MODAL_SECTION_IDS.details) ||
        (isMobile && activeSection === MODAL_SECTION_IDS.overview)) && (
        <SectionColumn id={isMobile ? MODAL_SECTION_IDS.overview : MODAL_SECTION_IDS.details}>
          {!isMobile && (
            <GalleryWrap>
              {/* MPH-935: the hero is not the only way into the photos — this modal opens its own
                  expandable deck. A Top Secret property has to stay hidden here too, so it gets the
                  same single blurred image behind the mark, with nothing to open. */}
              {staticData.isTopSecret ? (
                <EvoTopSecretGallery
                  images={uniqueImages}
                  css={css`
                    width: 100%;
                    height: 260px;
                    border-radius: 8px;
                  `}
                />
              ) : (
                <EvoHotelDetailsGallery
                  name={staticData.name}
                  images={uniqueImages}
                  css={css`
                    width: 100%;
                  `}
                />
              )}
            </GalleryWrap>
          )}

          <TwoColBlock>
            {showAiSummary ? (
              <>
                <LeftCol>
                  <GuestSummarizedReview />
                </LeftCol>
                <RightCol>
                  <CheckinCheckoutWithNights />
                  {hasWhatsAround && (
                    <>
                      <RightColDivider />
                      <EvoWhatsAround onSeeAll={handleSeeAll} />
                    </>
                  )}
                </RightCol>
              </>
            ) : (
              <>
                <LeftCol>
                  <CheckinCheckoutWithNights />
                </LeftCol>
                {hasWhatsAround && (
                  <>
                    <ColDivider />
                    <RightCol>
                      <EvoWhatsAround onSeeAll={handleSeeAll} />
                    </RightCol>
                  </>
                )}
              </>
            )}
          </TwoColBlock>

          {/* The hotel description leads the sections, in full: the modal is where travellers go to
              read it, so it has no "Read more" here (MPH-1246). */}
          {hasOverviewContent(staticData) && (
            <>
              <Divider />
              <HotelPageOverviewSection className="modal-flush-section" withCard={false} showCollapsed={false} />
            </>
          )}

          {/* Mobile only previews the most popular facilities here, so without them (new facilities)
              the block would be a bare link to the Facilities tab: leave it out. */}
          {(isMobile ? facilities.mostPopular.length > 0 : facilities.categories.length > 0) && (
            <>
              <Divider />
              {isMobile ? (
                <OverviewFacilitiesPreview
                  chips={facilities.mostPopular}
                  onSeeAll={seeAllTab(MODAL_SECTION_IDS.facilities)}
                />
              ) : (
                <EvoHotelFacilities className="modal-flush-section" />
              )}
            </>
          )}

          {showSurroundings && (
            <>
              <Divider />
              <EvoPropertySurroundings />
            </>
          )}

          {hasSustainability && (
            <>
              <Divider />
              <HotelPageEmissionsSectionEvo className="modal-flush-section" inModal={true} />
            </>
          )}

          {showBeforeYouBook && (
            <>
              <Divider />
              <HotelPageBeforeYouBookSectionEvo className="modal-flush-section" inModal={true} />
            </>
          )}
        </SectionColumn>
      )}

      {/* Mobile-only Facilities tab: the full list the Overview tab's "See all facilities" link
          points to. */}
      {isMobile && activeSection === MODAL_SECTION_IDS.facilities && facilities.categories.length > 0 && (
        <SectionColumn id={MODAL_SECTION_IDS.facilities}>
          <EvoHotelFacilities className="modal-flush-section" singleColumn={true} />
        </SectionColumn>
      )}

      {/* Mobile-only Location tab: What's nearby, then a divider, then address + map. */}
      {isMobile && activeSection === MODAL_SECTION_IDS.location && (
        <SectionColumn id={MODAL_SECTION_IDS.location}>
          {hasWhatsAround && (
            <>
              <EvoWhatsAround hideSeeAll={true} hideIcons={true} asTabSection={true} />
              <Divider />
            </>
          )}
          <MobileAddressMap>
            <EvoLocationAddress />
            <EvoLocationMap width={500} height={320} borderRadius={16} fullWidthButton={true} />
          </MobileAddressMap>
        </SectionColumn>
      )}

      {!reviewsDisabled && activeSection === MODAL_SECTION_IDS.reviews && (
        <SectionColumn id={MODAL_SECTION_IDS.reviews}>
          <HotelPageReviewSection className="modal-flush-section" language={localization.lang} inlineReviews={true} />
          {/* The provider (Expedia) reviews right below, where the classic page has them (MPH-602). */}
          <GuestReviewsDivider />
          <HotelPageGuestReviewsSection className="modal-flush-section" inline={true} />
        </SectionColumn>
      )}

      {activeSection === MODAL_SECTION_IDS.policies && (
        <SectionColumn id={MODAL_SECTION_IDS.policies}>
          <HotelPagePoliciesSection className="modal-flush-section" isEvo={true} />
        </SectionColumn>
      )}
    </Container>
  );
}
